import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';

import type { ApiClient } from '../api/client';
import type { Session, SessionTerminalWsTokenResponse } from '../types';
import ExecutorTerminalSessionDirectory, {
  compareExecutorTerminalSessions,
  executorTerminalSessionStatusDescription,
  executorTerminalSessionStatusLabel,
  isExecutorTerminalSessionAttachable,
  type TerminalSessionLastUsed,
  type ExecutorTerminalSessionGroup
} from './ExecutorTerminalSessionDirectory';
import {
  TerminalFullscreenExitButton,
  TerminalScrollModeControl,
  TerminalSpecialKeyToolbar,
  useTerminalFocusRecovery,
  useTerminalMaximize,
  useTerminalScrollMode,
  useTerminalSoftKeyModifiers,
  useTerminalTouchScroll
} from './TerminalControls';
import { TerminalFontSizeControl, useTerminalFontSize } from './TerminalFontSizePreference';
import { configureTerminalTextInput } from './TerminalTextInput';
import { installTerminalAndroidImeDiff } from './TerminalAndroidIme';
import {
  copyTerminalText,
  readTerminalClipboardText,
  TerminalSelectionActions,
  useTerminalTouchSelection,
  type TerminalTouchGestureOwner
} from './TerminalSelection';
import {
  formatExecutorTerminalProtocolError,
  formatExecutorTerminalTokenFailureMessage,
  formatWebSocketDisconnectedMessage,
  isTerminalIdentityMismatchError
} from '../utils/errors';
import { usePersistentTerminalConnection } from './usePersistentTerminalConnection';
import { useTerminalViewportDebug } from './TerminalViewportDebug';
import { shellQuoteTerminalPath, uploadTerminalFile } from './TerminalFileUpload';

type ExecutorTerminalPanelProps = {
  apiClient: ApiClient;
  executorId: string;
  setError: (message: string | null) => void;
  canUsePrivilegedTerminal: boolean;
  requestedSessionId?: string | null;
  lastUsedAt?: TerminalSessionLastUsed;
  workspaceGroups?: ExecutorTerminalSessionGroup[];
  workspaceLoading?: boolean;
  /** Increments only after a user-requested workspace refresh completes. */
  workspaceRefreshRevision?: number;
  onWorkspaceRefresh?: () => void;
  onWorkspaceSelect?: (executorId: string, sessionId: string | null) => void;
  onWorkspaceSessionsChange?: (executorId: string, sessions: Session[]) => void;
};

type ExecutorTerminalProfile = 'maintenance' | 'privileged';

const terminalOverviewRuler = () => ({
  width: window.matchMedia('(pointer: coarse)').matches ? 1 : 14
});

function terminalWsUrl(
  executorId: string,
  sessionId: string,
  token: string,
  requestId?: string | null,
  size?: { cols: number; rows: number } | null
) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams({ ws_token: token });
  if (requestId) params.set('client_request_id', requestId);
  if (size && size.cols > 0 && size.rows > 0) {
    params.set('cols', String(size.cols));
    params.set('rows', String(size.rows));
  }
  return (
    `${protocol}//${window.location.host}/api/executors/nodes/${encodeURIComponent(executorId)}` +
    `/terminal/sessions/${encodeURIComponent(sessionId)}/ws?${params.toString()}`
  );
}

export function executorTerminalSessionLabel(session: Session) {
  const profile = session.profile === 'privileged' ? 'Privileged' : 'Maintenance';
  const created = session.created_at.slice(0, 16).replace('T', ' ');
  const name = session.name?.trim() || `${profile} ${created}`;
  const status = ['attached', 'detached'].includes(session.status)
    ? ''
    : ` · ${executorTerminalSessionStatusLabel(session)}`;
  return `${name}${session.shared ? ' · shared' : ''}${status}`;
}

export default function ExecutorTerminalPanel({
  apiClient,
  executorId,
  setError: setGlobalError,
  canUsePrivilegedTerminal,
  requestedSessionId = null,
  lastUsedAt = {},
  workspaceGroups,
  workspaceLoading = false,
  workspaceRefreshRevision,
  onWorkspaceRefresh,
  onWorkspaceSelect,
  onWorkspaceSessionsChange
}: ExecutorTerminalPanelProps) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(requestedSessionId);
  const [profile, setProfile] = useState<ExecutorTerminalProfile>('maintenance');
  const [command, setCommand] = useState('bash -l');
  const [newSessionName, setNewSessionName] = useState('');
  const [shareNewSession, setShareNewSession] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [sessionShared, setSessionShared] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [removingSession, setRemovingSession] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [terminalIdentityMismatch, setTerminalIdentityMismatch] = useState(false);
  const [hasTerminalSelection, setHasTerminalSelection] = useState(false);
  const [terminalContextOpen, setTerminalContextOpen] = useState(false);
  const [terminalSelectionMode, setTerminalSelectionMode] = useState(false);
  const [sessionDirectoryOpen, setSessionDirectoryOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileUploadInputRef = useRef<HTMLInputElement | null>(null);
  const terminalHostRef = useRef<HTMLDivElement | null>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const terminalTouchGestureRef = useRef<TerminalTouchGestureOwner>('idle');
  const fitAddonRef = useRef<FitAddon | null>(null);
  const lastSizeRef = useRef<{ cols: number; rows: number } | null>(null);
  const appliedRequestedSessionRef = useRef<string | null>(null);
  const sessionLoadVersionRef = useRef(0);
  const sessionExecutorRef = useRef(executorId);
  const {
    panelRef,
    isMaximized,
    toggleMaximize,
    terminalViewportStyle,
    terminalKeyboardState,
    terminalKeyboardOverdrawGuard,
    terminalViewportFrameLayout
  } = useTerminalMaximize<HTMLElement>();
  const {
    enabled: terminalViewportDebugEnabled,
    pasteReport: pasteTerminalViewportDebugReport
  } = useTerminalViewportDebug({ isMaximized, panelRef, terminalRef });
  const { modifiers, toggleModifier, consumeShortcut, transformInput } = useTerminalSoftKeyModifiers();
  const { scrollMode, setScrollMode } = useTerminalScrollMode();
  const { fontSize } = useTerminalFontSize();

  useEffect(() => {
    if (sessionExecutorRef.current === executorId) return;
    sessionExecutorRef.current = executorId;
    sessionLoadVersionRef.current += 1;
    appliedRequestedSessionRef.current = null;
    setSessions([]);
    setSelectedSessionId(requestedSessionId);
    setSessionName('');
    setSessionShared(false);
    setError(null);
    terminalRef.current?.clear();
  }, [executorId, requestedSessionId]);

  const refreshSessions = useCallback(async (clearError = true): Promise<Session[] | null> => {
    const requestVersion = ++sessionLoadVersionRef.current;
    setLoading(true);
    try {
      const loaded = await apiClient.get<Session[]>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}/terminal/sessions`
      );
      if (requestVersion === sessionLoadVersionRef.current) {
        setSessions(loaded);
        onWorkspaceSessionsChange?.(executorId, loaded);
        if (clearError) setError(null);
        return loaded;
      }
      return null;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'failed to load executor terminal sessions';
      if (requestVersion === sessionLoadVersionRef.current) {
        setError(message);
        setGlobalError(message);
      }
      return null;
    } finally {
      if (requestVersion === sessionLoadVersionRef.current) setLoading(false);
    }
  }, [apiClient, executorId, onWorkspaceSessionsChange, setGlobalError]);

  useEffect(() => {
    void refreshSessions();
  }, [refreshSessions]);

  const orderedSessions = useMemo(
    () => [...sessions].sort((left, right) => compareExecutorTerminalSessions(left, right, lastUsedAt)),
    [lastUsedAt, sessions]
  );

  const selectedSession = useMemo(
    () =>
      orderedSessions.find((session) => session.id === selectedSessionId) ??
      orderedSessions.find(isExecutorTerminalSessionAttachable) ??
      orderedSessions[0] ??
      null,
    [orderedSessions, selectedSessionId]
  );
  const selectedId = selectedSession?.id ?? null;

  const {
    connectionState,
    connect: attach,
    disconnect: disconnectAttachment,
    stop: stopAttachment,
    send,
    fitAndSendResize: refitAndSendResize
  } = usePersistentTerminalConnection({
    requestToken: (sessionId) =>
      apiClient.post<SessionTerminalWsTokenResponse>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}` +
          `/terminal/sessions/${encodeURIComponent(sessionId)}/ws-token`
      ),
    socketUrl: (sessionId, token, requestId) =>
      terminalWsUrl(executorId, sessionId, token, requestId, lastSizeRef.current),
    fitAndSendResize,
    onText: (text) => terminalRef.current?.write(text),
    onBinary: (bytes) => terminalRef.current?.write(new Uint8Array(bytes)),
    onAttached: () => {
      // Reconnects are asynchronous and must not make unsolicited IME requests.
      // A terminal tap or soft key restores focus from a trusted gesture.
      void refreshSessions();
    },
    onDetached: () => {
      terminalRef.current?.clearSelection();
      void refreshSessions();
    },
    onProtocolError: (message, requestId) => {
      terminalRef.current?.clearSelection();
      setTerminalIdentityMismatch(false);
      const formatted = formatExecutorTerminalProtocolError(message, requestId);
      setError(formatted);
      setGlobalError(formatted);
      // The executor may have lost its tmux session during a host restart
      // while the durable control-plane row remained. Refresh immediately so
      // the UI can explain the terminal's terminal state and offer a clean
      // replacement instead of leaving an attachable-looking stale session.
      void refreshSessions(false);
    },
    onReconnectExhausted: ({ event, requestId, tokenError }) => {
      terminalRef.current?.clearSelection();
      setTerminalIdentityMismatch(tokenError !== undefined && isTerminalIdentityMismatchError(tokenError));
      const message =
        tokenError === undefined
          ? formatWebSocketDisconnectedMessage({
              context: 'Executor terminal disconnected',
              detail: 'The terminal could not reconnect automatically. Attach again to resume the executor PTY.',
              event,
              requestId
            })
          : formatExecutorTerminalTokenFailureMessage(
              'Executor terminal could not reconnect',
              tokenError,
              selectedSession?.can_manage !== false
            );
      setError(message);
      setGlobalError(message);
    }
  });
  const currentUploadTargetRef = useRef({ selectedId, connectionState });
  useEffect(() => {
    currentUploadTargetRef.current = { selectedId, connectionState };
  }, [connectionState, selectedId]);
  useTerminalTouchSelection(
    terminalHostRef,
    terminalRef,
    {
      enabled: true,
      gestureOwnerRef: terminalTouchGestureRef,
      immediate: isMaximized && terminalSelectionMode,
      onContextMenuChange: setTerminalContextOpen,
      onImmediateSelectionComplete: setTerminalSelectionMode
    }
  );
  useTerminalTouchScroll(
    terminalHostRef,
    terminalRef,
    true,
    undefined,
    scrollMode,
    terminalTouchGestureRef
  );
  const focusTerminal = useTerminalFocusRecovery(terminalHostRef, terminalRef);

  const connectionExecutorRef = useRef(executorId);
  useEffect(() => {
    if (connectionExecutorRef.current === executorId) return;
    connectionExecutorRef.current = executorId;
    disconnectAttachment();
  }, [disconnectAttachment, executorId]);

  useEffect(() => {
    const host = terminalHostRef.current;
    if (!host) return;
    const terminal = new Terminal({
      convertEol: false,
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize,
      overviewRuler: terminalOverviewRuler(),
      scrollback: 5000,
      theme: {
        background: '#101014',
        foreground: '#d9ffe0',
        cursor: '#8bd5ff'
      }
    });
    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(host);
    configureTerminalTextInput(terminal.textarea);
    // GBoard rewrites break xterm's naive textarea diffing; see module docs.
    const disposeAndroidImeDiff = installTerminalAndroidImeDiff(terminal);
    terminalRef.current = terminal;
    fitAddonRef.current = fitAddon;

    const sendResize = (cols: number, rows: number) => {
      if (cols < 1 || rows < 1) return;
      lastSizeRef.current = { cols, rows };
      send({ type: 'resize', cols, rows });
    };
    const fit = () => {
      try {
        fitAddon.fit();
      } catch {
        // The panel can be temporarily hidden during page layout changes.
      }
      sendResize(terminal.cols, terminal.rows);
    };
    const dataDisposable = terminal.onData((data) => {
      send({ type: 'input', data: transformInput(data) });
    });
    const selectionDisposable = terminal.onSelectionChange(() => {
      setHasTerminalSelection(terminal.hasSelection());
    });
    const resizeDisposable = terminal.onResize(({ cols, rows }) => sendResize(cols, rows));
    const resizeObserver = new ResizeObserver(fit);
    resizeObserver.observe(host);
    fit();
    return () => {
      resizeObserver.disconnect();
      dataDisposable.dispose();
      selectionDisposable.dispose();
      resizeDisposable.dispose();
      disposeAndroidImeDiff?.();
      terminal.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
    };
  // Font size is applied by the live update effect below so xterm scrollback is retained.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [send, transformInput]);

  useEffect(() => {
    const terminal = terminalRef.current;
    if (!terminal) return;
    terminal.options.fontSize = fontSize;
    const frame = window.requestAnimationFrame(() => refitAndSendResize());
    return () => window.cancelAnimationFrame(frame);
  }, [fontSize, refitAndSendResize]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      refitAndSendResize();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isMaximized, refitAndSendResize]);

  useEffect(() => {
    setSessionName(selectedSession?.name ?? '');
    setSessionShared(selectedSession?.shared === true);
  }, [selectedSession]);

  function fitAndSendResize(ws: WebSocket) {
    const terminal = terminalRef.current;
    if (terminal && fitAddonRef.current) {
      try {
        fitAddonRef.current.fit();
      } catch {
        // A later resize will retry if the current layout has no measurable width.
      }
      lastSizeRef.current = { cols: terminal.cols, rows: terminal.rows };
    }
    if (lastSizeRef.current && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'resize', ...lastSizeRef.current }));
    }
  }

  const connect = useCallback(async (sessionId = selectedId, candidate?: Session) => {
    if (!sessionId) return;
    const session = candidate ?? sessions.find((item) => item.id === sessionId);
    if (!session || !isExecutorTerminalSessionAttachable(session)) return;
    setError(null);
    setTerminalIdentityMismatch(false);
    await attach(sessionId);
  }, [attach, selectedId, sessions]);

  const refreshAndReconnect = useCallback(async () => {
    const sessionId = selectedId;
    const refreshedSessions = await refreshSessions();
    if (!sessionId || !refreshedSessions) return;
    const refreshedSession = refreshedSessions.find((session) => session.id === sessionId);
    if (refreshedSession && isExecutorTerminalSessionAttachable(refreshedSession)) {
      await connect(sessionId, refreshedSession);
    }
  }, [connect, refreshSessions, selectedId]);

  const appliedWorkspaceRefreshRevisionRef = useRef(workspaceRefreshRevision);
  useEffect(() => {
    if (
      workspaceRefreshRevision === undefined ||
      appliedWorkspaceRefreshRevisionRef.current === workspaceRefreshRevision
    ) {
      return;
    }
    appliedWorkspaceRefreshRevisionRef.current = workspaceRefreshRevision;
    void refreshAndReconnect();
  }, [refreshAndReconnect, workspaceRefreshRevision]);

  const activateWorkspaceSession = useCallback(async (session: Session, restoreFocus = true) => {
    if (restoreFocus) focusTerminal();
    disconnectAttachment();
    // Selecting a workspace session also writes its id to the URL. Record the
    // selection before that navigation so the requested-session effect does
    // not interpret the reflected URL as a second attach request. Terminal
    // WebSocket tokens are one-shot, so even a promptly cancelled duplicate
    // attach unnecessarily mints and consumes another token.
    appliedRequestedSessionRef.current = session.id;
    setSelectedSessionId(session.id);
    setSessionDirectoryOpen(false);
    setTerminalContextOpen(false);
    setError(null);
    setTerminalIdentityMismatch(false);
    terminalRef.current?.clear();
    onWorkspaceSelect?.(executorId, session.id);
    if (isExecutorTerminalSessionAttachable(session)) await attach(session.id);
  }, [attach, disconnectAttachment, executorId, focusTerminal, onWorkspaceSelect]);

  useEffect(() => {
    if (
      !requestedSessionId ||
      loading ||
      appliedRequestedSessionRef.current === requestedSessionId
    ) {
      return;
    }
    const requested = sessions.find((session) => session.id === requestedSessionId);
    if (!requested) return;
    appliedRequestedSessionRef.current = requestedSessionId;
    void activateWorkspaceSession(requested, false);
  }, [activateWorkspaceSession, loading, requestedSessionId, sessions]);

  const workspaceSessions = useMemo(
    () => (workspaceGroups ?? []).flatMap((group) =>
      group.sessions
        .filter(isExecutorTerminalSessionAttachable)
        .map((session) => ({ executorId: group.executor.id, session }))
    ),
    [workspaceGroups]
  );

  const selectWorkspaceTarget = useCallback((targetExecutorId: string, targetSessionId: string | null) => {
    if (targetExecutorId !== executorId || !targetSessionId) {
      focusTerminal();
      disconnectAttachment();
      setSessionDirectoryOpen(false);
      onWorkspaceSelect?.(targetExecutorId, targetSessionId);
      return;
    }
    const target = sessions.find((session) => session.id === targetSessionId);
    if (target) void activateWorkspaceSession(target);
  }, [activateWorkspaceSession, disconnectAttachment, executorId, focusTerminal, onWorkspaceSelect, sessions]);

  const selectNextWorkspaceSession = useCallback(() => {
    if (workspaceSessions.length < 2) return;
    const currentIndex = workspaceSessions.findIndex(
      (item) => item.executorId === executorId && item.session.id === selectedId
    );
    const next = workspaceSessions[(currentIndex + 1 + workspaceSessions.length) % workspaceSessions.length];
    selectWorkspaceTarget(next.executorId, next.session.id);
  }, [executorId, selectWorkspaceTarget, selectedId, workspaceSessions]);

  function disconnect() {
    terminalRef.current?.clearSelection();
    disconnectAttachment();
  }

  function sendTerminalInput(data: string) {
    send({ type: 'input', data });
  }

  function sendTerminalShortcut(key: Parameters<typeof consumeShortcut>[0]) {
    terminalRef.current?.clearSelection();
    sendTerminalInput(consumeShortcut(key));
  }

  function stopSession() {
    terminalRef.current?.clearSelection();
    stopAttachment();
  }

  async function startSession() {
    if (profile === 'privileged' && !command.trim()) return;
    terminalRef.current?.clearSelection();
    setStarting(true);
    setError(null);
    try {
      const session = await apiClient.post<Session>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}/terminal/sessions`,
        {
          profile,
          name: newSessionName.trim() || undefined,
          shared: shareNewSession,
          cols: lastSizeRef.current?.cols ?? 80,
          rows: lastSizeRef.current?.rows ?? 24,
          ...(profile === 'privileged' ? { command: command.trim() } : {})
        }
      );
      const nextSessions = [session, ...sessions.filter((item) => item.id !== session.id)];
      setSessions(nextSessions);
      onWorkspaceSessionsChange?.(executorId, nextSessions);
      setSelectedSessionId(session.id);
      appliedRequestedSessionRef.current = session.id;
      onWorkspaceSelect?.(executorId, session.id);
      setNewSessionName('');
      setShareNewSession(false);
      terminalRef.current?.clear();
      await connect(session.id, session);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'failed to start executor terminal';
      setError(message);
      setGlobalError(message);
    } finally {
      setStarting(false);
    }
  }

  async function saveSessionSettings() {
    if (!selectedSession) return;
    setSavingSession(true);
    setError(null);
    try {
      const updated = await apiClient.patch<Session>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}` +
          `/terminal/sessions/${encodeURIComponent(selectedSession.id)}`,
        { name: sessionName.trim() || null, shared: sessionShared }
      );
      const nextSessions = sessions.map((session) => session.id === updated.id ? updated : session);
      setSessions(nextSessions);
      onWorkspaceSessionsChange?.(executorId, nextSessions);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'failed to update executor terminal';
      setError(message);
      setGlobalError(message);
    } finally {
      setSavingSession(false);
    }
  }

  async function removeSession() {
    if (!selectedSession) return;
    const label = selectedSession.name?.trim() || 'this terminal session';
    if (!window.confirm(`Stop and permanently remove ${label}?`)) return;
    setRemovingSession(true);
    setError(null);
    try {
      disconnect();
      await apiClient.del<void>(
        `/api/executors/nodes/${encodeURIComponent(executorId)}` +
          `/terminal/sessions/${encodeURIComponent(selectedSession.id)}`
      );
      const nextSessions = sessions.filter((session) => session.id !== selectedSession.id);
      setSessions(nextSessions);
      onWorkspaceSessionsChange?.(executorId, nextSessions);
      setSelectedSessionId(null);
      setTerminalIdentityMismatch(false);
      setGlobalError(null);
      onWorkspaceSelect?.(executorId, null);
      terminalRef.current?.clear();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'failed to remove executor terminal';
      setError(message);
      setGlobalError(message);
    } finally {
      setRemovingSession(false);
    }
  }

  const toggleTerminalMaximize = useCallback(() => {
    setTerminalContextOpen(false);
    setTerminalSelectionMode(false);
    void toggleMaximize();
  }, [toggleMaximize]);

  const toggleTerminalSelectionMode = useCallback(() => {
    const terminal = terminalRef.current;
    terminal?.clearSelection();
    setTerminalSelectionMode((active) => {
      const next = !active;
      if (next) terminal?.blur();
      return next;
    });
  }, []);

  const copySelection = useCallback(async () => {
    const terminal = terminalRef.current;
    const selection = terminal?.getSelection() ?? '';
    if (!terminal || !selection) return;
    if (await copyTerminalText(selection)) {
      terminal.clearSelection();
      setTerminalContextOpen(false);
      setTerminalSelectionMode(false);
      return;
    }
    setGlobalError('Unable to copy the selected terminal text');
  }, [setGlobalError]);

  const pasteClipboard = useCallback(async () => {
    if (connectionState !== 'attached') return;
    const terminal = terminalRef.current;
    if (!terminal) return;
    const text = await readTerminalClipboardText();
    if (text === null) {
      setGlobalError('Unable to paste terminal clipboard. Allow clipboard access and try again.');
      return;
    }
    // Use xterm's public paste path: it normalizes newlines and honours the
    // remote program's bracketed-paste mode before emitting terminal input.
    try {
      terminal.paste(text);
      terminal.clearSelection();
      setTerminalContextOpen(false);
      setTerminalSelectionMode(false);
    } catch {
      setGlobalError('Unable to paste terminal clipboard. Allow clipboard access and try again.');
    }
  }, [connectionState, setGlobalError]);

  const requestFileUpload = useCallback(() => {
    if (connectionState !== 'attached' || !selectedId || uploadingFile) return;
    fileUploadInputRef.current?.click();
  }, [connectionState, selectedId, uploadingFile]);

  const handleFileUpload = useCallback(async (file: File | null) => {
    if (!file || !selectedId || connectionState !== 'attached') return;
    const uploadSessionId = selectedId;
    setUploadingFile(true);
    setError(null);
    try {
      const result = await uploadTerminalFile(apiClient, executorId, uploadSessionId, file);
      if (
        currentUploadTargetRef.current.selectedId !== uploadSessionId ||
        currentUploadTargetRef.current.connectionState !== 'attached'
      ) {
        // Do not paste a path into a different session if the user switched
        // terminals while the transfer was in flight. Preserve it on the
        // host clipboard instead so the successful upload remains usable.
        if (!(await copyTerminalText(result.path))) {
          setGlobalError(`Uploaded ${file.name} to ${result.path}`);
        }
        return;
      }
      terminalRef.current?.paste(shellQuoteTerminalPath(result.path));
      terminalRef.current?.clearSelection();
      setTerminalContextOpen(false);
      setTerminalSelectionMode(false);
      setGlobalError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'failed to upload terminal file';
      setError(message);
      setGlobalError(message);
    } finally {
      setUploadingFile(false);
    }
  }, [apiClient, connectionState, executorId, selectedId, setGlobalError]);

  return (
    <section
      ref={panelRef}
      className={`terminal-panel executor-terminal-panel${isMaximized ? ' terminal-panel--maximized' : ''}`}
      aria-label="Executor host terminal"
      style={terminalViewportStyle}
      data-terminal-keyboard={terminalKeyboardState}
      data-terminal-keyboard-guard={terminalKeyboardOverdrawGuard}
      data-terminal-viewport-frame={terminalViewportFrameLayout}
      data-terminal-touch-scroll="enabled"
      data-terminal-scroll-mode={scrollMode}
    >
      <div className="terminal-panel__viewport-frame">
        <TerminalFullscreenExitButton onExit={toggleTerminalMaximize} />
        <TerminalSelectionActions
          visible={isMaximized || terminalContextOpen}
          onCopy={() => void copySelection()}
          onPaste={() => void pasteClipboard()}
          copyDisabled={!hasTerminalSelection}
          pasteDisabled={connectionState !== 'attached'}
          selectionMode={terminalSelectionMode}
          onToggleSelectionMode={isMaximized ? toggleTerminalSelectionMode : undefined}
          onUpload={requestFileUpload}
          uploadDisabled={connectionState !== 'attached' || uploadingFile}
          uploadLabel={uploadingFile ? 'Uploading…' : 'Upload'}
        />
        <input
          ref={fileUploadInputRef}
          type="file"
          className="visually-hidden"
          aria-label="Choose a file to upload to the terminal"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0] ?? null;
            event.currentTarget.value = '';
            void handleFileUpload(file);
          }}
        />
        {workspaceGroups && sessionDirectoryOpen && (
          <div className="terminal-panel__session-drawer" role="dialog" aria-label="Switch terminal session">
            <div className="terminal-panel__session-drawer-header">
              <span>Terminal workspace</span>
              <button
                type="button"
                className="ghost"
                aria-label="Close terminal session switcher"
                onClick={() => setSessionDirectoryOpen(false)}
              >
                Close
              </button>
            </div>
            <ExecutorTerminalSessionDirectory
              groups={workspaceGroups}
              lastUsedAt={lastUsedAt}
              loading={workspaceLoading}
              onRefresh={onWorkspaceRefresh}
              onSelect={selectWorkspaceTarget}
              selectedExecutorId={executorId}
              selectedSessionId={selectedId}
            />
          </div>
        )}
        <div className="terminal-panel__header">
          <div className="terminal-panel__header-title">
            {workspaceGroups && (
              <button
                type="button"
                className="secondary compact terminal-panel__sessions-button"
                aria-label="Open terminal sessions"
                aria-expanded={sessionDirectoryOpen}
                title="Browse terminals on every executor"
                onClick={() => setSessionDirectoryOpen((open) => !open)}
              >
                Sessions
              </button>
            )}
            <div className="message-meta">
              <span>Executor host terminal</span>
              <span>{connectionState}</span>
            </div>
            {sessions.length > 0 && (
              <div className="terminal-panel__session-line">
                <select
                  aria-label="Executor terminal session"
                  value={selectedId ?? ''}
                  onChange={(event) => {
                    const sessionId = event.target.value || null;
                    const candidate = sessions.find((session) => session.id === sessionId);
                    if (workspaceGroups && candidate) {
                      void activateWorkspaceSession(candidate);
                      return;
                    }
                    disconnect();
                    setSelectedSessionId(sessionId);
                    setError(null);
                    setTerminalIdentityMismatch(false);
                    terminalRef.current?.clear();
                  }}
                >
                  {orderedSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {executorTerminalSessionLabel(session)}
                    </option>
                  ))}
                </select>
                {selectedSession?.can_manage !== false && (
                  <details className="terminal-panel__session-settings">
                    <summary aria-label="Manage terminal session" title="Rename, share, or remove terminal">
                      Manage
                    </summary>
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        void saveSessionSettings();
                      }}
                    >
                      <input
                        type="text"
                        aria-label="Terminal session name"
                        value={sessionName}
                        maxLength={80}
                        placeholder="Terminal name"
                        onChange={(event) => setSessionName(event.target.value)}
                        disabled={savingSession || removingSession}
                      />
                      <label className="terminal-panel__share-toggle">
                        <input
                          type="checkbox"
                          checked={sessionShared}
                          onChange={(event) => setSessionShared(event.target.checked)}
                          disabled={savingSession || removingSession}
                        />
                        Share with authorized users
                      </label>
                      <div className="row">
                        <button type="submit" disabled={savingSession || removingSession}>
                          {savingSession ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => void removeSession()}
                          disabled={savingSession || removingSession}
                        >
                          {removingSession ? 'Removing...' : 'Remove completely'}
                        </button>
                      </div>
                    </form>
                  </details>
                )}
              </div>
            )}
          </div>
          <div className="row">
            <button
              type="button"
              onClick={() => void connect()}
              disabled={
                !selectedSession ||
                !isExecutorTerminalSessionAttachable(selectedSession) ||
                connectionState === 'connecting'
              }
            >
              {connectionState === 'attached'
                ? 'Reconnect'
                : connectionState === 'reconnecting'
                  ? 'Retry now'
                  : 'Attach'}
            </button>
            <button type="button" className="ghost" onClick={disconnect} disabled={connectionState !== 'attached'}>
              Detach
            </button>
            <button type="button" className="secondary" onClick={stopSession} disabled={connectionState !== 'attached'}>
              Stop
            </button>
          </div>
        </div>
        {error && (
          <div className="message-error">
            <span>{error}</span>
            {terminalIdentityMismatch && selectedSession?.can_manage !== false && (
              <button
                type="button"
                className="danger compact"
                onClick={() => void removeSession()}
                disabled={removingSession}
              >
                {removingSession ? 'Removing...' : 'Remove session'}
              </button>
            )}
          </div>
        )}
        {selectedSession && !isExecutorTerminalSessionAttachable(selectedSession) &&
          executorTerminalSessionStatusDescription(selectedSession) && (
            <div className="terminal-panel__session-state" role="status">
              <strong>{executorTerminalSessionStatusLabel(selectedSession)}</strong>
              <span>{executorTerminalSessionStatusDescription(selectedSession)}</span>
              {selectedSession.can_manage !== false && (
                <span>Use “Start terminal” below to open a replacement.</span>
              )}
            </div>
          )}
        <form
          className={
            `terminal-panel__start executor-terminal-panel__start ` +
            `executor-terminal-panel__start--${profile}`
          }
          onSubmit={(event) => {
            event.preventDefault();
            void startSession();
          }}
        >
          <div className="terminal-panel__start-context">
            {canUsePrivilegedTerminal ? (
              <select
                aria-label="Terminal profile"
                value={profile}
                onChange={(event) => setProfile(event.target.value as ExecutorTerminalProfile)}
                disabled={starting}
              >
                <option value="maintenance">Maintenance</option>
                <option value="privileged">Privileged</option>
              </select>
            ) : (
              <span className="badge">Maintenance</span>
            )}
            {profile !== 'privileged' && <span className="hint">appuser shell</span>}
          </div>
          <input
            className="executor-terminal-panel__new-name"
            aria-label="New terminal name"
            value={newSessionName}
            maxLength={80}
            placeholder="Name (optional)"
            onChange={(event) => setNewSessionName(event.target.value)}
            disabled={starting}
          />
          <label className="terminal-panel__share-toggle terminal-panel__share-toggle--new">
            <input
              type="checkbox"
              checked={shareNewSession}
              onChange={(event) => setShareNewSession(event.target.checked)}
              disabled={starting}
            />
            Share
          </label>
          {profile === 'privileged' ? (
            <input
              className="executor-terminal-panel__command"
              aria-label="Host command"
              value={command}
              onChange={(event) => setCommand(event.target.value)}
              disabled={starting}
            />
          ) : null}
          <div className="terminal-panel__start-actions">
            <button type="submit" disabled={starting || (profile === 'privileged' && !command.trim())}>
              {starting
                ? 'Starting...'
                : selectedSession && !isExecutorTerminalSessionAttachable(selectedSession)
                  ? 'Start replacement terminal'
                  : 'Start terminal'}
            </button>
            <button
              type="button"
              className="ghost terminal-panel__refresh"
              onClick={() => void refreshAndReconnect()}
              disabled={loading || connectionState === 'connecting'}
              aria-label={loading ? 'Refreshing terminal sessions' : 'Refresh and reconnect'}
              title={loading ? 'Refreshing terminal sessions' : 'Refresh sessions and reconnect'}
            >
              ↻
            </button>
          </div>
        </form>
        <div className="terminal-panel__screen-wrap">
          <div className="terminal-panel__screen-actions">
            <button
              type="button"
              className="ghost terminal-panel__clipboard-action"
              onClick={() => void copySelection()}
              disabled={!hasTerminalSelection}
              aria-label="Copy selected terminal text"
              title="Copy selected terminal text"
            >
              Copy
            </button>
            <button
              type="button"
              className="ghost terminal-panel__clipboard-action"
              onClick={() => void pasteClipboard()}
              disabled={connectionState !== 'attached'}
              aria-label="Paste terminal clipboard"
              title="Paste terminal clipboard"
            >
              Paste
            </button>
            <button
              type="button"
              className="ghost terminal-panel__clipboard-action"
              onClick={requestFileUpload}
              disabled={connectionState !== 'attached' || uploadingFile}
              aria-label="Upload a file to the terminal"
              title="Upload into the active pane directory and paste its path"
            >
              {uploadingFile ? 'Uploading…' : 'Upload'}
            </button>
            <TerminalScrollModeControl mode={scrollMode} onChange={setScrollMode} />
            <TerminalFontSizeControl />
            <button
              type="button"
              className="ghost terminal-panel__maximize terminal-panel__screen-action"
              onClick={toggleTerminalMaximize}
              aria-pressed={isMaximized}
              aria-label={isMaximized ? 'Exit terminal full screen' : 'Enter terminal full screen'}
              title={isMaximized ? 'Exit full screen' : 'Full screen'}
            >
              ⛶
            </button>
          </div>
          <div
            ref={terminalHostRef}
            className="terminal-panel__screen"
            role="application"
            aria-label="Executor host terminal"
          />
        </div>
        <TerminalSpecialKeyToolbar
          disabled={connectionState !== 'attached'}
          onFocus={focusTerminal}
          modifiers={modifiers}
          onToggleModifier={toggleModifier}
          onShortcut={sendTerminalShortcut}
          debugAction={terminalViewportDebugEnabled ? {
            accessibleLabel: 'Paste terminal diagnostics',
            label: 'Debug',
            onActivate: pasteTerminalViewportDebugReport,
            title: 'Paste viewport diagnostics without sending Enter'
          } : undefined}
          workspaceActions={workspaceGroups ? [
            {
              accessibleLabel: 'Switch terminal session',
              label: 'Sessions',
              onActivate: () => setSessionDirectoryOpen((open) => !open),
              pressed: sessionDirectoryOpen,
              title: 'Browse terminals on every executor'
            },
            {
              accessibleLabel: 'Switch to next terminal session',
              disabled: workspaceSessions.length < 2,
              label: 'Next',
              onActivate: selectNextWorkspaceSession,
              title: 'Attach the next available terminal'
            }
          ] : undefined}
        />
      </div>
    </section>
  );
}
